import { darkmode } from '@components'
import { api } from '@here/api'
import DOMPurify from 'dompurify'
import { type JSX, createResource } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { css } from 'styled-system/css'
import { Box } from 'styled-system/jsx'
import { ZoomableImage } from '../../../../components/custom/ZoomableImage'
import 'media-chrome' // registers <media-*> custom elements
import { AudioPlayer } from './AudioPlayer'

// (Optional) TS: allow media-chrome custom elements in TSX
declare module 'solid-js' {
	namespace JSX {
		interface IntrinsicElements {
			'media-controller': any
			'media-time-display': any
			'media-duration-display': any
			'media-play-button': any
			'media-mute-button': any
			'media-volume-range': any
			'media-time-range': any
			'media-playback-rate-button': any
			'media-seek-backward-button': any
			'media-seek-forward-button': any
		}
	}
}

// Signed URL cache: key = original src
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>()
const SIGNED_URL_TTL_SECS = 60 * 60 // 1 hour

function ImageWrapper(props: { src: string; alt: string }): JSX.Element {
	return (
		<ZoomableImage src={props.src}>
			<img
				src={props.src}
				alt={props.alt}
			/>
		</ZoomableImage>
	)
}

type RichTextProps = {
	content: string
	class?: string
}

function autoLinkUrls(html: string): string {
	const urlRegex = /(?<!["'=])\b(https?:\/\/[^\s<>"']+)/g
	return html.replace(urlRegex, (url) => {
		return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
	})
}

function embedTradingViewCharts(raw: string): string {
	const withLinks = autoLinkUrls(raw)
	return withLinks.replace(
		/<a[^>]+href="(https:\/\/www\.tradingview\.com\/x\/([a-zA-Z0-9]+)\/?)"[^>]*>[^<]*<\/a>/g,
		(_, fullUrl, chartId) => {
			const imgSrc = `https://www.tradingview.com/x/${chartId}?png`
			return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${fullUrl}</a>
        <img src="${imgSrc}" alt="TradingView chart" />`
		},
	)
}

async function enhanceWithSignedUrls(html: string): Promise<string> {
	const parser = new DOMParser()
	const doc = parser.parseFromString(html, 'text/html')

	const imgTags = Array.from(doc.querySelectorAll('img'))
	const audioTags = Array.from(doc.querySelectorAll('audio'))

	const now = Date.now()

	const processTag = async (el: HTMLImageElement | HTMLAudioElement) => {
		const src = el.getAttribute('src')
		if (
			!src ||
			src.startsWith('data:') ||
			src.startsWith('blob:') ||
			src.includes('token=') ||
			src.includes('tradingview.com/x/')
		) {
			return
		}
		const cached = signedUrlCache.get(src)
		if (cached && cached.expiresAt > now) {
			el.setAttribute('src', cached.url)
			return
		}

		const { data, error } = await api()
			.storage.from('channel-files')
			.createSignedUrl(src, SIGNED_URL_TTL_SECS)

		if (error) {
			console.warn(`Failed to sign URL for: ${src}`, error)
			return
		}

		if (data?.signedUrl) {
			signedUrlCache.set(src, {
				url: data.signedUrl,
				expiresAt: now + SIGNED_URL_TTL_SECS * 1000,
			})
			el.setAttribute('src', data.signedUrl)
		}
	}

	await Promise.all([...imgTags, ...audioTags].map(processTag))
	return doc.body.innerHTML
}

function convertHtmlToJsx(html: string): JSX.Element[] {
	const parser = new DOMParser()
	const doc = parser.parseFromString(html, 'text/html')
	const body = doc.body

	function walk(node: ChildNode): JSX.Element | string | null {
		if (node.nodeType === Node.TEXT_NODE) return node.textContent
		if (node.nodeType !== Node.ELEMENT_NODE) return null

		const el = node as HTMLElement
		const children = Array.from(el.childNodes).map(walk).filter(Boolean)

		if (el.tagName.toLowerCase() === 'img') {
			const src = el.getAttribute('src') ?? ''
			const alt = el.getAttribute('alt') ?? ''
			return (
				<ImageWrapper
					src={src}
					alt={alt}
				/>
			)
		}

		// 🔄 Replace native <audio> with Media Chrome
		if (el.tagName.toLowerCase() === 'audio') {
			// Preserve original attributes and children (e.g., <source>)
			const props = Object.fromEntries([...el.attributes].map((a) => [a.name, a.value]))

			// You can theme Media Chrome with CSS variables if desired
			return (
				<AudioPlayer {...props} />
				// <media-controller class={css({
				//   minW: {
				//     base: '400px'
				//   },
				//   height: '50px'
				// })}>
				//   {/* The real media element stays native but gets slotted for Media Chrome */}
				//   {/* We keep children so <source> tags etc. continue to work */}
				//   <audio slot="media" {...props}>
				//     {children}
				//   </audio>

				//   {/* Controls row */}
				//   <Box>
				//     <media-play-button />
				//     <media-seek-backward-button seconds={10} />
				//     <media-seek-forward-button seconds={10} />
				//     <media-time-range style={{ flex: 1 }} />
				//     <media-time-display showduration />
				//     <media-mute-button />
				//     <media-volume-range style={{ width: '120px' }} />
				//     {/* Replaces your custom speed selector */}
				//     <media-playback-rate-button playbackrates="0.5,0.75,1,1.25,1.5,2" />
				//   </Box>
				// </media-controller>
			)
		}

		const tag = el.tagName.toLowerCase()
		const props = Object.fromEntries(
			[...el.attributes].map((attr) => [attr.name, attr.value]),
		)

		return (
			<Dynamic
				component={tag}
				{...props}
			>
				{children}
			</Dynamic>
		)
	}

	return Array.from(body.childNodes).map(walk).filter(Boolean) as JSX.Element[]
}

export default function RichText(props: RichTextProps): JSX.Element {
	const [enhancedHtml] = createResource(() => {
		const withAutoLinkedUrls = autoLinkUrls(props.content)
		const withEmbeddedTradingView = embedTradingViewCharts(withAutoLinkedUrls)
		return DOMPurify.sanitize(withEmbeddedTradingView, {
			USE_PROFILES: { html: true },
			ADD_ATTR: ['target', 'rel'],
		})
	}, enhanceWithSignedUrls)

	return (
		<div
			class={css({
				fontSize: 'sm',
				color: 'gray.800',
				lineHeight: '1.7',
				'& strong': { fontWeight: 'bold' },
				'& a': { color: darkmode() ? 'tscGreen.10' : 'tscGreen.8' },
				'& em': { fontStyle: 'italic' },
				'& u': { textDecoration: 'underline' },
				'& ol': { listStyleType: 'decimal', paddingLeft: '5', marginBottom: '4' },
				'& ul': { listStyleType: 'disc', paddingLeft: '5', marginBottom: '4' },
				'& li': { marginBottom: '2' },
				'& img': { maxW: '200px' },
			})}
		>
			{enhancedHtml.latest ? convertHtmlToJsx(enhancedHtml.latest) : null}
		</div>
	)
}
